import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  Tooltip,
  type ChartData,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { AlertTriangle, ChevronDown, Clock, Loader2, X } from 'lucide-react';
import type { CrmLeadMetrics } from '../../hooks/useCrmLeadMetrics';
import {
  LEAD_STATUS_COLORS,
  useCrmLeadsMonthDetails,
  type LeadsDayRow,
  type LeadStatus,
} from '../../hooks/useCrmLeadsMonthDetails';
import { useCrmLeadsBySourcePie } from '../../hooks/useCrmLeadsBySourcePie';
import { useOpenLeadsByStage } from '../../hooks/useOpenLeadsByStage';
import { useNewLeadsByMonthYtdBreakdown } from '../../hooks/useNewLeadsByMonthYtdBreakdown';
import { useWinRateByMonthYtdBreakdown } from '../../hooks/useWinRateByMonthYtdBreakdown';
import { useConversionByMonthYtdBreakdown } from '../../hooks/useConversionByMonthYtdBreakdown';
import { useAvgDealSizeByMonthYtdBreakdown } from '../../hooks/useAvgDealSizeByMonthYtdBreakdown';
import { useAvgTimeToCloseByMonthYtdBreakdown } from '../../hooks/useAvgTimeToCloseByMonthYtdBreakdown';
import { useRevenueWonByMonthYtdBreakdown } from '../../hooks/useRevenueWonByMonthYtdBreakdown';
import { SOURCE_PIE_CHART_COLORS } from '../../lib/crm/sourcePieChart';
import { formatCurrency } from '../../lib/utils';
import {
  formatDays,
  formatDuration,
  relativeTrend,
  TrendChip,
} from './crmReportPrimitives';
import CrmEmbeddedOpportunityModal from './CrmEmbeddedOpportunityModal';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

const STATUS_ORDER: LeadStatus[] = ['Open', 'Won', 'Lost'];

const STATUS_PILL: Record<LeadStatus, string> = {
  Open: 'bg-blue-100 text-blue-700',
  Won: 'bg-emerald-100 text-emerald-700',
  Lost: 'bg-red-100 text-red-700',
};

interface CrmLeadsMonthModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Already loaded by the hero banner, so the ledger and KPIs cost no extra queries. */
  metrics: CrmLeadMetrics;
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
      {children}
    </h3>
  );
}

function StatBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] text-gray-500">{label}</p>
      <p className="text-sm font-semibold tabular-nums tracking-tight text-gray-900">{value}</p>
    </div>
  );
}

/** Label + value + trend chip, mirroring the pipeline health cards it replaces. */
function HealthStat({
  label,
  value,
  current,
  previous,
  invert,
}: {
  label: string;
  value: string;
  current?: number;
  previous?: number;
  invert?: boolean;
}) {
  const trend =
    current != null && previous != null ? relativeTrend(current, previous, { invert }) : null;
  return (
    <div className="flex items-start justify-between gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2">
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-[0.05em] text-gray-400">
          {label}
        </p>
        <p className="mt-0.5 text-[17px] font-bold leading-none text-gray-900 tabular-nums">
          {value}
        </p>
      </div>
      <TrendChip trend={trend} />
    </div>
  );
}

interface ShareRow {
  key: string;
  label: string;
  count: number;
  /** Money, when the row has any. */
  value?: number;
  share: number;
  color: string;
}

function ShareRows({
  rows,
  countNoun,
  emptyCopy,
  loading,
}: {
  rows: ShareRow[];
  countNoun: string;
  emptyCopy: string;
  loading?: boolean;
}) {
  if (loading && rows.length === 0) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="h-5 w-5 animate-spin text-gray-300" />
      </div>
    );
  }
  if (rows.length === 0) {
    return <p className="text-sm text-gray-500 text-center py-6">{emptyCopy}</p>;
  }
  return (
    <div className="divide-y divide-gray-100">
      {rows.map((row) => (
        <div key={row.key} className="px-3 py-2.5">
          <div className="flex items-baseline justify-between gap-3">
            <span className="inline-flex items-baseline gap-1.5 text-sm font-medium text-gray-800 min-w-0">
              <span
                className="h-2 w-2 self-center rounded-full shrink-0"
                style={{ backgroundColor: row.color }}
              />
              <span className="truncate">{row.label}</span>
            </span>
            <span className="text-sm font-semibold text-gray-900 tabular-nums shrink-0">
              {row.count} {countNoun}
              {row.count === 1 ? '' : 's'}
              {row.value != null && row.value > 0 ? (
                <span className="ml-1.5 text-[11px] font-normal text-gray-500">
                  {formatCurrency(row.value)}
                </span>
              ) : null}
              <span className="ml-1.5 text-[11px] font-normal text-gray-400">
                {(row.share * 100).toFixed(0)}%
              </span>
            </span>
          </div>
          <div className="mt-1.5 h-1.5 rounded-full bg-gray-100 overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{ width: `${Math.max(row.share * 100, 1.5)}%`, backgroundColor: row.color }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function DailyLeadsChart({ days }: { days: LeadsDayRow[] }) {
  const activeStatuses = STATUS_ORDER.filter((status) => days.some((d) => d[status] > 0));
  const chartData: ChartData<'bar', number[], string> = {
    labels: days.map((d) => d.label),
    datasets: activeStatuses.map((status) => ({
      label: status === 'Open' ? 'Still open' : status,
      data: days.map((d) => d[status]),
      backgroundColor: LEAD_STATUS_COLORS[status],
      borderRadius: 3,
      maxBarThickness: 16,
    })),
  };

  return (
    <div className="h-[200px] w-full">
      <Bar
        data={chartData}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                title: (items) => {
                  const day = days[items[0]?.dataIndex ?? -1];
                  if (!day) return '';
                  return `${day.label} — ${day.total} ${day.total === 1 ? 'lead' : 'leads'}`;
                },
                label: (ctx) => `${ctx.dataset.label}: ${ctx.formattedValue}`,
              },
            },
          },
          scales: {
            x: {
              stacked: true,
              grid: { display: false },
              ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 12, font: { size: 10 } },
            },
            y: {
              stacked: true,
              grid: { color: 'rgba(0,0,0,0.06)' },
              ticks: { font: { size: 10 }, precision: 0 },
            },
          },
        }}
      />
    </div>
  );
}

type YtdMetricKey =
  | 'newLeads'
  | 'winRate'
  | 'conversion'
  | 'avgDeal'
  | 'timeToClose'
  | 'revenue';

interface YtdMonthPoint {
  monthKey: string;
  label: string;
  value: number;
  isCurrentMonth: boolean;
}

const YTD_METRICS: { key: YtdMetricKey; label: string }[] = [
  { key: 'newLeads', label: 'New leads' },
  { key: 'winRate', label: 'Win rate' },
  { key: 'conversion', label: 'Conversion' },
  { key: 'avgDeal', label: 'Avg deal' },
  { key: 'timeToClose', label: 'Time to close' },
  { key: 'revenue', label: 'Revenue won' },
];

/**
 * Year-to-date monthly trends. Absorbs the six standalone "by month" modals by
 * calling their existing hooks; only the selected metric ever loads.
 */
function YtdTrends() {
  const [metric, setMetric] = useState<YtdMetricKey>('newLeads');

  const newLeads = useNewLeadsByMonthYtdBreakdown();
  const winRate = useWinRateByMonthYtdBreakdown();
  const conversion = useConversionByMonthYtdBreakdown();
  const avgDeal = useAvgDealSizeByMonthYtdBreakdown();
  const timeToClose = useAvgTimeToCloseByMonthYtdBreakdown();
  const revenue = useRevenueWonByMonthYtdBreakdown();

  const asCount = (n: number) => String(Math.round(n));
  const asPercent = (n: number) => `${Math.round(n)}%`;

  const config: Record<
    YtdMetricKey,
    {
      months: YtdMonthPoint[];
      loading: boolean;
      error: string | null;
      load: () => Promise<void>;
      format: (value: number) => string;
      yearLabel: string;
      yearValue: string;
      yearCaption: string;
    }
  > = {
    newLeads: {
      months: newLeads.months.map((m) => ({ ...m, value: m.count })),
      loading: newLeads.loading,
      error: newLeads.error,
      load: newLeads.load,
      format: asCount,
      yearLabel: newLeads.yearLabel,
      yearValue: String(newLeads.yearTotal),
      yearCaption: 'leads this year',
    },
    winRate: {
      months: winRate.months.map((m) => ({ ...m, value: m.winRate })),
      loading: winRate.loading,
      error: winRate.error,
      load: winRate.load,
      format: asPercent,
      yearLabel: winRate.yearLabel,
      yearValue: asPercent(winRate.yearWinRate),
      yearCaption: `${winRate.yearWon} won · ${winRate.yearLost} lost`,
    },
    conversion: {
      months: conversion.months.map((m) => ({ ...m, value: m.rate })),
      loading: conversion.loading,
      error: conversion.error,
      load: conversion.load,
      format: asPercent,
      yearLabel: conversion.yearLabel,
      yearValue: asPercent(conversion.yearRate),
      yearCaption: `${conversion.yearWon} of ${conversion.yearCreated} created`,
    },
    avgDeal: {
      months: avgDeal.months.map((m) => ({ ...m, value: m.avgValue })),
      loading: avgDeal.loading,
      error: avgDeal.error,
      load: avgDeal.load,
      format: formatCurrency,
      yearLabel: avgDeal.yearLabel,
      yearValue: formatCurrency(avgDeal.yearAvg),
      yearCaption: `across ${avgDeal.yearCount} won deals`,
    },
    timeToClose: {
      months: timeToClose.months.map((m) => ({ ...m, value: m.avgDays })),
      loading: timeToClose.loading,
      error: timeToClose.error,
      load: timeToClose.load,
      format: formatDays,
      yearLabel: timeToClose.yearLabel,
      yearValue: formatDays(timeToClose.yearAvgDays),
      yearCaption: `across ${timeToClose.yearCount} won deals`,
    },
    revenue: {
      months: revenue.months.map((m) => ({ ...m, value: m.total })),
      loading: revenue.loading,
      error: revenue.error,
      load: revenue.load,
      format: formatCurrency,
      yearLabel: revenue.yearLabel,
      yearValue: formatCurrency(revenue.yearTotal),
      yearCaption: `from ${revenue.yearCount} won deals`,
    },
  };

  const active = config[metric];
  const activeLoad = active.load;

  useEffect(() => {
    void activeLoad();
  }, [activeLoad]);

  const chartData: ChartData<'bar', number[], string> = {
    labels: active.months.map((m) => m.label.replace(/ \d{4}$/, '')),
    datasets: [
      {
        label: YTD_METRICS.find((m) => m.key === metric)?.label ?? '',
        data: active.months.map((m) => m.value),
        backgroundColor: active.months.map((m) => (m.isCurrentMonth ? '#2563eb' : '#93c5fd')),
        borderRadius: 3,
        maxBarThickness: 28,
      },
    ],
  };

  return (
    <div className="border-t border-gray-100 p-3 space-y-3">
      <div className="flex flex-wrap items-center gap-1">
        {YTD_METRICS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => setMetric(key)}
            className={`rounded-lg px-2 py-1 text-[11px] font-semibold transition-colors ${
              metric === key
                ? 'bg-blue-50 text-blue-700 border border-blue-200'
                : 'border border-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="flex items-baseline gap-2">
        <span className="text-[17px] font-bold text-gray-900 tabular-nums">
          {active.loading && active.months.length === 0 ? '—' : active.yearValue}
        </span>
        <span className="text-[11px] text-gray-500">
          {active.yearLabel} · {active.yearCaption}
        </span>
      </div>

      {active.error ? (
        <p className="py-6 text-center text-sm text-red-600">{active.error}</p>
      ) : active.loading && active.months.length === 0 ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-gray-300" />
        </div>
      ) : active.months.length === 0 ? (
        <p className="py-6 text-center text-sm text-gray-500">No data this year yet.</p>
      ) : (
        <>
          <div className="h-[180px] w-full">
            <Bar
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    callbacks: {
                      label: (ctx) => active.format(ctx.raw as number),
                    },
                  },
                },
                scales: {
                  x: { grid: { display: false }, ticks: { font: { size: 10 } } },
                  y: {
                    grid: { color: 'rgba(0,0,0,0.06)' },
                    ticks: { font: { size: 10 } },
                  },
                },
              }}
            />
          </div>
          <ul className="divide-y divide-gray-50 rounded-lg border border-gray-200">
            {active.months.map((month) => (
              <li
                key={month.monthKey}
                className={`flex items-baseline justify-between gap-3 px-3 py-1.5 text-sm ${
                  month.isCurrentMonth ? 'bg-blue-50/60' : ''
                }`}
              >
                <span
                  className={month.isCurrentMonth ? 'font-semibold text-blue-900' : 'text-gray-700'}
                >
                  {month.label}
                </span>
                <span
                  className={`tabular-nums ${
                    month.isCurrentMonth ? 'font-bold text-blue-900' : 'font-semibold text-gray-900'
                  }`}
                >
                  {active.format(month.value)}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function winRatePill(winRate: number, hasClosed: boolean) {
  if (!hasClosed) return { label: 'No data yet', pill: 'bg-gray-100 text-gray-500' };
  if (winRate >= 60) return { label: 'Healthy', pill: 'bg-emerald-50 text-emerald-700' };
  if (winRate >= 40) return { label: 'Steady', pill: 'bg-amber-50 text-amber-700' };
  return { label: 'Needs attention', pill: 'bg-red-50 text-red-700' };
}

/** Inner content — mounted only while open so its hooks don't fetch in the background. */
function LeadsModalContent({
  onClose,
  metrics,
  detailLeadId,
  onSelectLead,
}: {
  onClose: () => void;
  metrics: CrmLeadMetrics;
  detailLeadId: number | null;
  onSelectLead: (leadId: number | null) => void;
}) {
  const { summary, days, outcomes, feedDays, monthLabel, loading, error, load } =
    useCrmLeadsMonthDetails();
  const [sourceView, setSourceView] = useState<'new' | 'won'>('new');
  const { data: sourceData, loading: sourceLoading } = useCrmLeadsBySourcePie(sourceView);
  const { data: lostData, loading: lostLoading } = useCrmLeadsBySourcePie('lost');
  const { stages, total: stageTotal, loading: stagesLoading } = useOpenLeadsByStage();
  const [feedExpanded, setFeedExpanded] = useState(false);
  const [ytdExpanded, setYtdExpanded] = useState(false);

  useEffect(() => {
    void load();
  }, [load]);

  const leadsTrend = relativeTrend(metrics.newLeadsThisMonth, metrics.newLeadsLastMonth);
  const closedThisMonth = metrics.leadsWonThisMonth + metrics.leadsLostThisMonth;
  const pill = winRatePill(metrics.winRateThisMonth, closedThisMonth > 0);

  const sourceRows: ShareRow[] = sourceData.sourceDetails.map((detail, index) => ({
    key: detail.source,
    label: detail.source,
    count: detail.count,
    value: detail.value,
    share: sourceData.total > 0 ? detail.count / sourceData.total : 0,
    color: SOURCE_PIE_CHART_COLORS[index % SOURCE_PIE_CHART_COLORS.length],
  }));

  const lostRows: ShareRow[] = lostData.sourceDetails.map((detail, index) => ({
    key: detail.source,
    label: detail.source,
    count: detail.count,
    value: detail.value,
    share: lostData.total > 0 ? detail.count / lostData.total : 0,
    color: SOURCE_PIE_CHART_COLORS[index % SOURCE_PIE_CHART_COLORS.length],
  }));

  const stageRows: ShareRow[] = stages.map((stage, index) => ({
    key: stage.name,
    label: stage.name,
    count: stage.count,
    share: stageTotal > 0 ? stage.count / stageTotal : 0,
    color: SOURCE_PIE_CHART_COLORS[index % SOURCE_PIE_CHART_COLORS.length],
  }));

  const outcomeRows: ShareRow[] = outcomes.map((outcome) => ({
    key: outcome.key,
    label: outcome.label,
    count: outcome.count,
    value: outcome.value,
    share: outcome.share,
    color: LEAD_STATUS_COLORS[outcome.key],
  }));

  // Ledger identity: opening balance + new = open now + won + lost.
  const ledgerIn = metrics.openingBalanceLeads + metrics.newLeadsThisMonth;
  const ledgerOut =
    metrics.openLeads + metrics.leadsWonThisMonth + metrics.leadsLostThisMonth;

  return (
    <>
      <div
        className={`relative w-full max-w-3xl max-h-[min(92vh,760px)] flex flex-col rounded-2xl border border-gray-200/90 bg-white shadow-[0_20px_60px_rgba(0,0,0,0.18)] overflow-hidden ${
          detailLeadId != null ? 'invisible' : ''
        }`}
        style={{
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-3 border-b border-gray-100 shrink-0">
          <div className="min-w-0">
            <h2 id="crm-leads-month-title" className="text-base font-semibold text-gray-900">
              New leads this month
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">{monthLabel || 'Month to date'}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Headline + attention chips */}
        <div className="px-4 py-3 bg-gray-50/80 border-b border-gray-100 shrink-0">
          <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                New leads this month
              </p>
              <div className="flex items-baseline gap-2 mt-0.5">
                <p className="text-2xl font-bold text-gray-900 tabular-nums tracking-tight">
                  {metrics.loading ? '—' : metrics.newLeadsThisMonth}
                </p>
                <TrendChip trend={leadsTrend} />
              </div>
              <p className="text-[11px] text-gray-500 mt-0.5">
                vs {metrics.newLeadsLastMonth} last month
              </p>
            </div>
            <div className="flex flex-wrap items-start gap-x-5 gap-y-2 sm:gap-x-7">
              <StatBlock label="Today" value={String(summary.leadsToday)} />
              <StatBlock label="Avg per day" value={summary.avgPerDay.toFixed(1)} />
              <StatBlock
                label="Projected month-end"
                value={String(Math.round(summary.projectedMonthEnd))}
              />
              <StatBlock label="Pipeline value" value={formatCurrency(summary.pipelineValue)} />
            </div>
          </div>

          {metrics.overdueFollowUpsCount > 0 || metrics.stalledDealsCount > 0 ? (
            <div className="mt-2.5 pt-2.5 border-t border-gray-200/70 flex flex-wrap items-center gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                Needs attention
              </span>
              {metrics.overdueFollowUpsCount > 0 ? (
                <span className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2 py-1 text-[12px] font-semibold text-red-700">
                  <Clock className="h-3.5 w-3.5" />
                  {metrics.overdueFollowUpsCount} overdue follow-up
                  {metrics.overdueFollowUpsCount === 1 ? '' : 's'}
                </span>
              ) : null}
              {metrics.stalledDealsCount > 0 ? (
                <span className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2 py-1 text-[12px] font-semibold text-amber-700">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {metrics.stalledDealsCount} stalled
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Scroll body */}
        <div className="flex-1 min-h-0 overflow-y-auto ios-momentum-scroll px-4 py-4 space-y-5">
          {loading && days.length === 0 ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-7 w-7 animate-spin text-gray-300" />
            </div>
          ) : error ? (
            <p className="text-sm text-red-600 text-center py-10">{error}</p>
          ) : (
            <>
              {/* Daily new leads */}
              <section>
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                  <SectionTitle>Daily new leads</SectionTitle>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
                    {STATUS_ORDER.filter((status) => days.some((d) => d[status] > 0)).map(
                      (status) => (
                        <span key={status} className="inline-flex items-center gap-1.5">
                          <span
                            className="h-2 w-2 rounded-full"
                            style={{ backgroundColor: LEAD_STATUS_COLORS[status] }}
                          />
                          {status === 'Open' ? 'Still open' : status}
                        </span>
                      ),
                    )}
                  </div>
                </div>
                <p className="mt-1 text-[11px] text-gray-400">
                  Bars are the day each lead came in, coloured by where it stands now.
                </p>
                <div className="mt-2 rounded-xl border border-gray-200 bg-white p-3">
                  {days.every((d) => d.total === 0) ? (
                    <p className="text-sm text-gray-500 text-center py-8">
                      No leads created this month.
                    </p>
                  ) : (
                    <DailyLeadsChart days={days} />
                  )}
                </div>
              </section>

              {/* Where they came from (new / won by source) */}
              <section>
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
                  <SectionTitle>Where they came from</SectionTitle>
                  <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
                    {(
                      [
                        ['new', 'New'],
                        ['won', 'Won'],
                      ] as const
                    ).map(([mode, label]) => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => setSourceView(mode)}
                        className={`rounded-md px-2 py-0.5 text-[11px] font-semibold transition-colors ${
                          sourceView === mode
                            ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <p className="mt-1 text-[11px] text-gray-400">
                  {sourceView === 'new'
                    ? 'Leads created this month, by source.'
                    : 'Deals won this month, by the source that produced them.'}
                </p>
                <div className="mt-2 rounded-xl border border-gray-200 bg-white overflow-hidden">
                  <ShareRows
                    rows={sourceRows}
                    countNoun="lead"
                    emptyCopy={
                      sourceView === 'new' ? 'No leads yet.' : 'Nothing won this month yet.'
                    }
                    loading={sourceLoading}
                  />
                </div>
              </section>

              {/* Cohort outcomes + open by stage */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <section>
                  <SectionTitle>What happened to them</SectionTitle>
                  <div className="mt-2 rounded-xl border border-gray-200 bg-white overflow-hidden">
                    <ShareRows
                      rows={outcomeRows}
                      countNoun="lead"
                      emptyCopy="No leads yet."
                    />
                    {summary.totalLeads > 0 ? (
                      <div className="flex items-baseline justify-between gap-3 border-t border-gray-100 px-3 py-2 text-sm">
                        <span className="text-gray-600">Conversion rate</span>
                        <span className="font-semibold text-gray-900 tabular-nums">
                          {summary.conversionRate.toFixed(0)}%
                        </span>
                      </div>
                    ) : null}
                    <p className="px-3 pb-2 text-[11px] text-gray-400">
                      Of the leads created this month.
                    </p>
                  </div>
                </section>

                <section>
                  <SectionTitle>Open pipeline by stage</SectionTitle>
                  <div className="mt-2 rounded-xl border border-gray-200 bg-white overflow-hidden">
                    <ShareRows
                      rows={stageRows}
                      countNoun="lead"
                      emptyCopy="No open leads."
                      loading={stagesLoading}
                    />
                    <p className="px-3 pb-2 pt-2 text-[11px] text-gray-400 border-t border-gray-100">
                      All open leads, not just this month's.
                    </p>
                  </div>
                </section>
              </div>

              {/* Why we lost */}
              <section>
                <SectionTitle>Why we lost</SectionTitle>
                <p className="mt-1 text-[11px] text-gray-400">
                  Leads marked lost this month, by reason.
                </p>
                <div className="mt-2 rounded-xl border border-gray-200 bg-white overflow-hidden">
                  <ShareRows
                    rows={lostRows}
                    countNoun="lead"
                    emptyCopy="Nothing lost this month."
                    loading={lostLoading}
                  />
                </div>
              </section>

              {/* Pipeline health */}
              <section>
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                  <SectionTitle>Pipeline health</SectionTitle>
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${pill.pill}`}
                  >
                    {pill.label}
                  </span>
                </div>
                <div className="mt-2 grid grid-cols-2 lg:grid-cols-3 gap-2">
                  <HealthStat
                    label="Open pipeline"
                    value={formatCurrency(metrics.openPipelineValue)}
                  />
                  <HealthStat
                    label="Win rate"
                    value={`${Math.round(metrics.winRateThisMonth)}%`}
                    current={metrics.winRateThisMonth}
                    previous={metrics.winRateLastMonth}
                  />
                  <HealthStat
                    label="Revenue won"
                    value={formatCurrency(metrics.revenueWonThisMonth)}
                    current={metrics.revenueWonThisMonth}
                    previous={metrics.revenueWonLastMonth}
                  />
                  <HealthStat
                    label="Avg deal size"
                    value={formatCurrency(metrics.avgWonDealSize)}
                    current={metrics.avgWonDealSize}
                    previous={metrics.avgWonDealSizeLastMonth}
                  />
                  <HealthStat
                    label="Time to close"
                    value={formatDays(metrics.avgTimeToCloseDays)}
                    current={metrics.avgTimeToCloseDays}
                    previous={metrics.avgTimeToCloseDaysLastMonth}
                    invert
                  />
                  <HealthStat
                    label="Time to first contact"
                    value={formatDuration(metrics.avgTimeToFirstContactDays)}
                    current={metrics.avgTimeToFirstContactDays}
                    previous={metrics.avgTimeToFirstContactDaysLastMonth}
                    invert
                  />
                </div>
              </section>

              {/* Ledger */}
              <section>
                <SectionTitle>Inside the number</SectionTitle>
                <div className="mt-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm">
                  <div className="flex items-baseline justify-between gap-3 py-1.5">
                    <span className="text-gray-600">Opening balance (open on the 1st)</span>
                    <span className="font-semibold text-blue-700 tabular-nums">
                      {metrics.openingBalanceLeads}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between gap-3 py-1.5">
                    <span className="text-gray-600">New leads this month</span>
                    <span className="font-semibold text-gray-900 tabular-nums">
                      + {metrics.newLeadsThisMonth}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between gap-3 py-1.5 border-t border-gray-100">
                    <span className="font-medium text-gray-800">Total worked</span>
                    <span className="font-bold text-gray-900 tabular-nums">{ledgerIn}</span>
                  </div>
                  <div className="flex items-baseline justify-between gap-3 py-1.5 border-t border-gray-100">
                    <span className="text-gray-600">Still open</span>
                    <span className="font-semibold text-blue-700 tabular-nums">
                      {metrics.openLeads}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between gap-3 py-1.5">
                    <span className="text-gray-600">Won this month</span>
                    <span className="font-semibold text-emerald-700 tabular-nums">
                      {metrics.leadsWonThisMonth}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between gap-3 py-1.5">
                    <span className="text-gray-600">Lost this month</span>
                    <span className="font-semibold text-red-700 tabular-nums">
                      {metrics.leadsLostThisMonth}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between gap-3 py-1.5 border-t border-gray-100">
                    <span className="font-medium text-gray-800">Accounted for</span>
                    <span
                      className={`font-bold tabular-nums ${
                        ledgerOut === ledgerIn ? 'text-gray-900' : 'text-amber-700'
                      }`}
                    >
                      {ledgerOut}
                    </span>
                  </div>
                </div>
              </section>

              {/* Leads feed */}
              <section>
                <div className="mt-2 rounded-xl border border-gray-200 bg-white overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setFeedExpanded((v) => !v)}
                    className="flex w-full items-center justify-between gap-3 px-3 py-2.5 hover:bg-gray-50 transition-colors"
                    aria-expanded={feedExpanded}
                  >
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      Leads this month
                      {summary.totalLeads > 0 ? ` · ${summary.totalLeads}` : ''}
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 text-gray-400 transition-transform ${
                        feedExpanded ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {!feedExpanded ? null : feedDays.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-6 border-t border-gray-100">
                      No leads created this month.
                    </p>
                  ) : (
                    <div className="border-t border-gray-100">
                      {feedDays.map((day) => (
                        <div key={day.date}>
                          <div
                            className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide border-b border-gray-100 ${
                              day.isToday
                                ? 'bg-blue-50/80 text-blue-700'
                                : 'bg-gray-50/80 text-gray-500'
                            }`}
                          >
                            {day.label}
                            {day.isToday ? ' · Today' : ''}
                          </div>
                          <ul className="divide-y divide-gray-50">
                            {day.rows.map((row) => (
                              <li key={row.id}>
                                <button
                                  type="button"
                                  onClick={() => onSelectLead(row.id)}
                                  className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-gray-50 transition-colors"
                                >
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium text-gray-800 truncate">
                                      {row.title}
                                    </p>
                                    <p className="text-[11px] text-gray-500 truncate">
                                      {row.subtitle}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-2 shrink-0">
                                    {row.value > 0 ? (
                                      <span className="text-sm font-semibold text-gray-900 tabular-nums">
                                        {formatCurrency(row.value)}
                                      </span>
                                    ) : null}
                                    <span
                                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                        STATUS_PILL[row.status]
                                      }`}
                                    >
                                      {row.status}
                                    </span>
                                  </div>
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>

              {/* Year to date trends — mounted (and fetched) only on expand */}
              <section>
                <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setYtdExpanded((v) => !v)}
                    className="flex w-full items-center justify-between gap-3 px-3 py-2.5 hover:bg-gray-50 transition-colors"
                    aria-expanded={ytdExpanded}
                  >
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      Year to date trends
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 text-gray-400 transition-transform ${
                        ytdExpanded ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {ytdExpanded ? <YtdTrends /> : null}
                </div>
              </section>
            </>
          )}
        </div>
      </div>

    </>
  );
}

/** Full CRM lead picture for the month — the deep dive behind the New leads tile. */
export default function CrmLeadsMonthModal({
  isOpen,
  onClose,
  metrics,
}: CrmLeadsMonthModalProps) {
  // Held here so the drawer renders as a sibling of this dialog (the CRM drawer
  // portals to body at a lower z-index, so it must not sit inside this layer).
  const [detailLeadId, setDetailLeadId] = useState<number | null>(null);
  /** Bumped after a lead edit to refetch the month's data. */
  const [contentKey, setContentKey] = useState(0);

  useEffect(() => {
    if (!isOpen) setDetailLeadId(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && detailLeadId == null) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose, detailLeadId]);

  if (!isOpen) return null;

  const drawerOpen = detailLeadId != null;

  return createPortal(
    <>
      {/* While the lead drawer is up this stays mounted (keeping scroll and
          expanded sections) but lets clicks through to the drawer beneath. */}
      <div
        className={`fixed inset-0 z-[99998] flex items-center justify-center p-3 sm:p-4 ${
          drawerOpen ? 'pointer-events-none' : ''
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="crm-leads-month-title"
      >
        <div
          className={`absolute inset-0 bg-black/30 backdrop-blur-sm ${
            drawerOpen ? 'invisible' : ''
          }`}
          onClick={onClose}
          aria-hidden
        />
        <LeadsModalContent
          key={contentKey}
          onClose={onClose}
          metrics={metrics}
          detailLeadId={detailLeadId}
          onSelectLead={setDetailLeadId}
        />
      </div>

      {drawerOpen ? (
        <CrmEmbeddedOpportunityModal
          opportunityId={detailLeadId as number}
          onClose={() => setDetailLeadId(null)}
          onUpdated={() => {
            void metrics.refresh();
            setContentKey((key) => key + 1);
          }}
        />
      ) : null}
    </>,
    document.body
  );
}
